import React, {
  Suspense,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { Canvas, useLoader, useFrame, useThree } from "@react-three/fiber";
import { useProgress, Html, shaderMaterial } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import * as THREE from "three";
import "../styles/MobileRenderer.css";
import { extend } from "@react-three/fiber";
import { DeviceOrientationControls } from "../DeviceOrientationControls";

// Shader materiał dla fali teleportacji
const TeleportationWaveMaterial = shaderMaterial(
  {
    time: 0,
    color: new THREE.Color(0.2, 0.5, 1.0),
    point: new THREE.Vector3(0, 0, 0),
  },
  // vertex shader
  `
    varying vec2 vUv;
    varying vec3 vPosition;
    uniform vec3 point;

    void main() {
      vUv = uv;
      vPosition = position;

      // Dodaj lekkie wybrzuszenie w miejscu kliknięcia
      float dist = distance(position.xz, point.xz);
      float elevation = smoothstep(0.0, 2.0, dist) * smoothstep(4.0, 2.0, dist) * 0.2;
      vec3 pos = position;
      pos.y += elevation;

      gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    }
  `,
  // fragment shader
  `
    uniform float time;
    uniform vec3 color;
    uniform vec3 point;
    varying vec2 vUv;
    varying vec3 vPosition;

    void main() {
      // Oblicz odległość od punktu kliknięcia w płaszczyźnie XZ
      float dist = distance(vPosition.xz, point.xz);

      // Stwórz kilka koncentrycznych fal
      float wave1 = sin(dist * 4.0 - time * 12.0) * 0.5 + 0.5;
      float wave2 = sin(dist * 8.0 - time * 15.0) * 0.25 + 0.25;
      float wave3 = sin(dist * 12.0 - time * 18.0) * 0.125 + 0.125;

      // Połącz fale
      float waves = wave1 + wave2 + wave3;

      // Stwórz gradient zanikania od środka
      float fadeOut = (1.0 - time) * smoothstep(4.0, 0.0, dist);

      // Dodaj świecenie w centrum
      float glow = smoothstep(1.0, 0.0, dist) * 0.5;

      // Połącz wszystkie efekty
      vec3 finalColor = color + vec3(glow);
      float alpha = (waves * 0.5 + glow) * fadeOut;

      // Dodaj efekt refrakcji
      float refraction = sin(dist * 10.0 - time * 20.0) * 0.02;
      alpha *= 1.0 + refraction;

      gl_FragColor = vec4(finalColor, alpha * 0.8);
    }
  `,
);

extend({ TeleportationWaveMaterial });

// Komponent fali teleportacji
function TeleportationWave({ point }) {
  const meshRef = useRef();
  const materialRef = useRef();
  const [active, setActive] = useState(true);
  const startTime = useRef(Date.now());

  useFrame(() => {
    if (!active || !materialRef.current) return;

    const elapsed = (Date.now() - startTime.current) / 1000;
    materialRef.current.time = elapsed;

    if (elapsed > 1) {
      setActive(false);
    }
  });

  if (!active) return null;

  return (
    <mesh
      ref={meshRef}
      rotation={[-Math.PI / 2, 0, 0]}
      position={[point.x, 0.01, point.z]}
    >
      <circleGeometry args={[4, 64]} />
      <teleportationWaveMaterial
        ref={materialRef}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        point={[point.x, point.y, point.z]}
        color={new THREE.Color(0.2, 0.5, 1.0)}
      />
    </mesh>
  );
}

function LoadingIndicator() {
  const { progress } = useProgress();
  const [isLoading, setIsLoading] = useState(true);

  // Dodajemy efekt, który zatrzyma animację po załadowaniu
  useEffect(() => {
    if (progress >= 100) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [progress]);

  return (
    <Html center>
      <div
        className="loader-container"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "50px",
          marginTop: "-35vh",
          backgroundColor: "rgba(0,0,0,0.7)",
          padding: "30px",
          borderRadius: "10px",
        }}
      >
        <div className="loading-text">Ładowanie wnętrza</div>
        <div
          className="percentage"
          style={{ fontSize: "1.2em", fontWeight: "bold" }}
        >
          {Math.round(progress)}%
        </div>
        <div className={`funny-loader ${!isLoading ? "loaded" : ""}`}>
          <div className="inner-circle"></div>
        </div>
      </div>
    </Html>
  );
}

const Model = ({ setWalls }) => {
  const gltf = useLoader(GLTFLoader, "/models/showcase_room.glb");
  const modelRef = useRef();
  const [lightsConfigured, setLightsConfigured] = useState(false);
  const originalIntensities = useRef(new Map());

  useEffect(() => {
    if (gltf && !lightsConfigured) {
      const wallObjects = [];

      // Najpierw znajdź obiekt 'walls' (rodzic)
      let wallsParent = null;
      gltf.scene.traverse((node) => {
        if (node.name && node.name.toLowerCase() === "walls") {
          wallsParent = node;
          console.log("Znaleziono obiekt-rodzic 'walls':", wallsParent);
        }
      });

      // Przeszukaj model w poszukiwaniu ścian i skonfiguruj cienie
      gltf.scene.traverse((node) => {
        if (node.isMesh) {
          node.castShadow = true;
          node.receiveShadow = true;

          // Identyfikuj ściany - sprawdź czy jest dzieckiem obiektu 'walls'
          // LUB ma w nazwie słowo wall/ściana/itp.
          if (
            (wallsParent &&
              node.parent &&
              (node.parent === wallsParent ||
                node.parent.parent === wallsParent)) ||
            node.name.toLowerCase().includes("wall") ||
            node.name.toLowerCase().includes("sciana") ||
            node.name.toLowerCase().includes("ściana") ||
            node.name.toLowerCase().includes("col") ||
            node.name.toLowerCase().includes("floor") ||
            node.name.toLowerCase().includes("ceiling") ||
            node.name.toLowerCase().includes("sufit") ||
            node.name.toLowerCase().includes("podloga") ||
            node.name.toLowerCase().includes("podłoga")
          ) {
            console.log(
              "Dodaję ścianę do kolizji:",
              node.name,
              "rodzic:",
              node.parent ? node.parent.name : "brak",
            );
            node.geometry.computeBoundingBox();
            const box = node.geometry.boundingBox.clone();
            box.applyMatrix4(node.matrixWorld);

            const wallInfo = {
              id: node.name,
              mesh: node,
              box: box,
              position: node.position.clone(),
              scale: node.scale.clone(),
            };
            wallObjects.push(wallInfo);
          }
        }
        // Identyfikuj światła punktowe
        else if (node.isLight) {
          // Zapisz oryginalną intensywność
          originalIntensities.current.set(node.uuid, node.intensity);

          // Zmniejsz intensywność świateł o 0.005 oryginalnej wartości
          const originalIntensity = node.intensity;
          node.intensity = originalIntensity * 0.005;
        }
      });

      // Zaktualizuj listę ścian w głównym komponencie
      setWalls(wallObjects);
      console.log("Wykryto ścian:", wallObjects.length);
      setLightsConfigured(true);
    }
  }, [gltf, lightsConfigured, setWalls]);

  if (!gltf) return null;

  return <primitive object={gltf.scene} ref={modelRef} />;
};

// Funkcja sprawdzająca kolizje - identyczna jak w wersji desktopowej
function checkCollision(start, end, walls, doors = [], radius = 0.3) {
  // Tablice obiektów kolizyjnych
  const wallMeshes = walls.filter((w) => w.mesh).map((w) => w.mesh);
  const doorMeshes = doors.filter((d) => d.mesh).map((d) => d.mesh);
  const allColliders = [...wallMeshes, ...doorMeshes];

  // Jeśli nie ma obiektów kolizyjnych, loguj błąd
  if (allColliders.length === 0) {
    console.warn("checkCollision: Brak obiektów kolizyjnych!");
    return false;
  }

  // Konwertuj parametry na Vector3, jeśli są tablicami
  const startVec = Array.isArray(start)
    ? new THREE.Vector3(start[0], start[1], start[2])
    : start.clone();

  const endVec = Array.isArray(end)
    ? new THREE.Vector3(end[0], end[1], end[2])
    : end.clone();

  // Kierunek i odległość ruchu
  const moveVec = new THREE.Vector3().subVectors(endVec, startVec);
  const direction = moveVec.clone().normalize();
  const distance = moveVec.length();

  // Podstawowy promień sprawdzania kolizji
  const raycaster = new THREE.Raycaster(
    startVec,
    direction,
    0,
    distance + radius,
  );
  const hits = raycaster.intersectObjects(allColliders, true);

  if (hits.length > 0) {
    return true;
  }

  // Dodatkowe promienie dla lepszej detekcji kolizji
  const sideAngles = [
    Math.PI / 4,
    Math.PI / 2,
    (3 * Math.PI) / 4,
    Math.PI,
    (5 * Math.PI) / 4,
    (3 * Math.PI) / 2,
    (7 * Math.PI) / 4,
  ];

  for (const angle of sideAngles) {
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;
    const offsetStart = startVec.clone().add(new THREE.Vector3(x, 0, z));

    const sideRaycaster = new THREE.Raycaster(
      offsetStart,
      direction,
      0,
      distance,
    );
    const sideHits = sideRaycaster.intersectObjects(allColliders, true);

    if (sideHits.length > 0 && sideHits[0].distance <= distance) {
      return true;
    }
  }

  return false;
}

// Kontrolery dotykowe z obsługą orientacji urządzenia
function TouchControls({
  walls,
  doors,
  setIsLookingAround,
  enableDeviceOrientation,
  teleportEnabled,
  updateTeleportWavePoint,
}) {
  const { camera, gl, scene, raycaster, pointer } = useThree();

  // Inicjalizacja pozycji kamery - wnętrze pokoju, na wysokości oczu, patrząc w stronę kominka i mebli
  useEffect(() => {
    camera.position.set(0, 1.65, 2.0);
    camera.rotation.set(0, 0, 0);
  }, [camera]);

  const [useDeviceOrientation, setUseDeviceOrientation] = useState(false);
  const deviceControls = useRef(null);
  const touchStartPosition = useRef({ x: 0, y: 0 });
  const touchLastPosition = useRef({ x: 0, y: 0 });
  const isSwiping = useRef(false);
  const swipeThreshold = 10;
  const rotationSpeed = 0.01;
  const time = useRef(Date.now());
  const euler = useRef(new THREE.Euler(0, 0, 0, "YXZ"));
  const [teleportPoint, setTeleportPoint] = useState(null);
  const [teleporting, setTeleporting] = useState(false);
  const teleportTarget = useRef(null);
  const teleportStartTime = useRef(0);
  const [rightMousePressed, setRightMousePressed] = useState(false);
  const rightMousePosition = useRef({ x: 0, y: 0 });
  const isVRMode = useRef(false);

  // Udostępnij kamerę dla głównego komponentu
  useEffect(() => {
    window.mobileCamera = camera;
  }, [camera]);

  // Aktualizuj stan useDeviceOrientation na podstawie propa enableDeviceOrientation
  useEffect(() => {
    setUseDeviceOrientation(enableDeviceOrientation);
    isVRMode.current = enableDeviceOrientation;

    // Logi do debugowania
    if (enableDeviceOrientation) {
      console.log("Aktywowano tryb AR/VR - sterowanie żyroskopem włączone");
    } else {
      console.log("Dezaktywowano tryb AR/VR - sterowanie żyroskopem wyłączone");
    }
  }, [enableDeviceOrientation]);

  // Inicjalizacja eulera zgodnie z pozycją startową kamery
  useEffect(() => {
    euler.current.set(0, 0, 0);
  }, [camera]);

  // Obsługa DeviceOrientationControls
  useEffect(() => {
    if (useDeviceOrientation) {
      try {
        console.log(
          "Inicjalizacja kontrolera orientacji DeviceOrientationControls",
        );

        // Tworzenie nowej instancji kontrolera
        deviceControls.current = new DeviceOrientationControls(camera);

        // Sprawdzamy czy kontroler został poprawnie zainicjalizowany
        if (!deviceControls.current) {
          console.error("Nie udało się zainicjalizować kontrolera orientacji.");
          return;
        }

        // Bezpieczne podłączenie kontrolera
        try {
          deviceControls.current.connect();
          console.log("Kontroler orientacji pomyślnie podłączony");
        } catch (error) {
          console.error(
            "Błąd podczas łączenia z kontrolerem orientacji:",
            error,
          );
          deviceControls.current = null;
          return;
        }

        // Funkcja czyszcząca
        return () => {
          if (deviceControls.current) {
            console.log("Odłączanie kontrolera orientacji");
            try {
              deviceControls.current.disconnect();
              deviceControls.current = null;
            } catch (error) {
              console.error(
                "Błąd podczas odłączania kontrolera orientacji:",
                error,
              );
            }
          }
        };
      } catch (error) {
        console.error(
          "Błąd podczas inicjalizacji kontrolera orientacji:",
          error,
        );
        deviceControls.current = null;
      }
    }
  }, [camera, useDeviceOrientation]);

  useFrame(() => {
    const currentTime = Date.now();
    time.current = currentTime;

    // Obsługa sterowania urządzeniem (żyroskop)
    if (useDeviceOrientation && deviceControls.current) {
      try {
        // Aktualizacja kontrolera orientacji dla trybu AR/VR
        deviceControls.current.update();
      } catch (error) {
        console.error(
          "Błąd podczas aktualizacji kontrolera orientacji:",
          error,
        );
        deviceControls.current = null;
        setUseDeviceOrientation(false);
        isVRMode.current = false;
      }
    }

    // Obsługa teleportacji
    if (teleporting && teleportTarget.current) {
      const now = Date.now();
      const elapsed = now - teleportStartTime.current;
      const duration = 500; // 0.5 sekundy na teleportację
      const progress = Math.min(elapsed / duration, 1);

      // Użyj funkcji ease-out dla płynniejszego ruchu
      const easeOutProgress = 1 - Math.pow(1 - progress, 3);

      // Interpoluj pozycję
      const startPos = camera.position.clone();
      const targetPos = teleportTarget.current.position;
      const newPos = new THREE.Vector3(
        startPos.x + (targetPos.x - startPos.x) * easeOutProgress,
        startPos.y,
        startPos.z + (targetPos.z - startPos.z) * easeOutProgress,
      );

      // Ustaw nową pozycję
      camera.position.copy(newPos);

      if (progress >= 1) {
        setTeleporting(false);
        setTeleportPoint(null);
        teleportTarget.current = null;
      }
    }
  });

  // Obsługa zdarzeń dotykowych
  useEffect(() => {
    const canvas = gl.domElement;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      touchStartPosition.current = { x: touch.clientX, y: touch.clientY };
      touchLastPosition.current = { x: touch.clientX, y: touch.clientY };

      // Sprawdź czy teleportacja jest włączona - jeśli nie, nie wykonuj teleportacji
      if (!teleportEnabled) {
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera({ x, y }, camera);

      // Znajdź obiekty podłogi
      const floorObjects = [];
      scene.traverse((object) => {
        if (
          object.isMesh &&
          (object.name.toLowerCase().includes("floor") ||
            object.name.toLowerCase().includes("ground"))
        ) {
          floorObjects.push(object);
        }
      });

      // Sprawdź przecięcie z podłogą
      const intersects = raycaster.intersectObjects(floorObjects);

      if (intersects.length > 0) {
        const point = intersects[0].point;
        const currentHeight = camera.position.y;
        teleportTarget.current = {
          position: new THREE.Vector3(point.x, currentHeight, point.z),
        };
        setTeleportPoint(point);
        setTeleporting(true);
        teleportStartTime.current = Date.now();
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 1 && !useDeviceOrientation) {
        const touch = e.touches[0];
        const deltaX = touch.clientX - touchLastPosition.current.x;
        const deltaY = touch.clientY - touchLastPosition.current.y;

        if (!isSwiping.current) {
          const diffX = touch.clientX - touchStartPosition.current.x;
          const diffY = touch.clientY - touchStartPosition.current.y;

          if (
            Math.abs(diffX) > swipeThreshold ||
            Math.abs(diffY) > swipeThreshold
          ) {
            isSwiping.current = true;
          }
        }

        if (isSwiping.current) {
          euler.current.y -= deltaX * rotationSpeed;
          euler.current.x -= deltaY * rotationSpeed;
          euler.current.x = Math.max(
            -Math.PI / 2 + 0.1,
            Math.min(Math.PI / 2 - 0.1, euler.current.x),
          );
          camera.quaternion.setFromEuler(euler.current);
          setIsLookingAround(true);
        }

        touchLastPosition.current = { x: touch.clientX, y: touch.clientY };
      }

      e.preventDefault();
    };

    const handleTouchEnd = (e) => {
      isSwiping.current = false;
      setIsLookingAround(false);

      if (teleportEnabled && !teleporting) {
        // Tworzymy tymczasowy raycaster dla dotyku
        const touch = e.changedTouches[0];
        const rect = canvas.getBoundingClientRect();
        const x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;

        // Ustaw raycaster dla punktu dotyku
        raycaster.setFromCamera({ x, y }, camera);

        // Znajdujemy obiekty podłogi w scenie
        const floorObjects = [];
        scene.traverse((object) => {
          if (
            object.isMesh &&
            (object.name.toLowerCase().includes("floor") ||
              object.name.toLowerCase().includes("ground"))
          ) {
            floorObjects.push(object);
          }
        });

        // Sprawdzamy przecięcie z podłogą
        const intersects = raycaster.intersectObjects(floorObjects);

        if (intersects.length > 0) {
          const point = intersects[0].point;
          // Dodaj dodatkowe sprawdzenia, aby upewnić się, że punkt jest w zakresie sceny
          const isValidPoint = !checkCollision(
            [camera.position.x, camera.position.y, camera.position.z],
            [point.x, 1.7, point.z],
            walls,
            doors,
          );

          if (isValidPoint) {
            // Utwórz punkt teleportacji jako obiekt Three.js Vector3
            const teleportPoint = new THREE.Vector3(point.x, point.y, point.z);
            updateTeleportWavePoint(teleportPoint);

            // Rozpocznij teleportację
            teleportTarget.current = {
              position: new THREE.Vector3(point.x, camera.position.y, point.z),
            };
            setTeleportPoint(point);
            setTeleporting(true);
            teleportStartTime.current = Date.now();

            console.log("Teleportacja do punktu:", point);
          }
        }
      }
    };

    canvas.addEventListener("touchstart", handleTouchStart, { passive: false });
    canvas.addEventListener("touchmove", handleTouchMove, { passive: false });
    canvas.addEventListener("touchend", handleTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener("touchstart", handleTouchStart);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    camera,
    gl,
    raycaster,
    useDeviceOrientation,
    setIsLookingAround,
    teleportEnabled,
    teleporting,
    scene,
    walls,
    doors,
    updateTeleportWavePoint,
    setTeleportPoint,
  ]);

  // Dodaj obsługę kliknięcia lewym przyciskiem myszy dla teleportacji
  useEffect(() => {
    const handleLeftClick = (e) => {
      if (e.button !== 0 || teleporting || !teleportEnabled) return;
      raycaster.setFromCamera(pointer, camera);
      const floorObjects = [];
      scene.traverse((object) => {
        if (
          object.isMesh &&
          (object.name.toLowerCase().includes("floor") ||
            object.name.toLowerCase().includes("ground"))
        ) {
          floorObjects.push(object);
        }
      });

      // Sprawdź przecięcie z podłogą
      const intersects = raycaster.intersectObjects(floorObjects);

      if (intersects.length > 0) {
        const point = intersects[0].point;

        // Dodaj dodatkowe sprawdzenia, aby upewnić się, że punkt jest w zakresie sceny
        const isValidPoint = !checkCollision(
          [camera.position.x, camera.position.y, camera.position.z],
          [point.x, 1.7, point.z],
          walls,
          doors,
        );

        if (isValidPoint) {
          // Utwórz punkt teleportacji jako obiekt Three.js Vector3
          const teleportPoint = new THREE.Vector3(point.x, point.y, point.z);
          updateTeleportWavePoint(teleportPoint);

          // Rozpocznij teleportację
          const currentHeight = camera.position.y;
          teleportTarget.current = {
            position: new THREE.Vector3(point.x, currentHeight, point.z),
          };
          setTeleportPoint(point);
          setTeleporting(true);
          teleportStartTime.current = Date.now();
        }
      }
    };

    gl.domElement.addEventListener("mousedown", handleLeftClick);

    return () => {
      gl.domElement.removeEventListener("mousedown", handleLeftClick);
    };
  }, [
    raycaster,
    pointer,
    camera,
    gl,
    scene,
    teleporting,
    teleportEnabled,
    walls,
    doors,
    updateTeleportWavePoint,
  ]);

  // Dodaj obsługę prawego przycisku myszy dla obracania kamery
  useEffect(() => {
    const canvas = gl.domElement;

    const handleContextMenu = (e) => {
      e.preventDefault(); // Zapobiegaj wyświetlaniu menu kontekstowego

      const tempEuler = new THREE.Euler(0, 0, 0, "YXZ");
      tempEuler.setFromQuaternion(camera.quaternion, "YXZ");
      euler.current.copy(tempEuler);

      setRightMousePressed(true);
      rightMousePosition.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e) => {
      if (!rightMousePressed) return;

      // Oblicz przemieszczenie myszy
      const deltaX = e.clientX - rightMousePosition.current.x;
      const deltaY = e.clientY - rightMousePosition.current.y;

      // Sprawdź czy zmiana nie jest zbyt duża (może wskazywać na skok myszy)
      if (Math.abs(deltaX) > 100 || Math.abs(deltaY) > 100) {
        rightMousePosition.current = { x: e.clientX, y: e.clientY };
        return;
      }

      // Zredukowana prędkość obrotu dla większej precyzji
      const adjustedRotationSpeed = rotationSpeed * 0.5;

      // Aktualizuj kątowe położenie kamery (zastosuj obrót)
      euler.current.y -= deltaX * adjustedRotationSpeed;
      euler.current.x -= deltaY * adjustedRotationSpeed;

      // Ogranicz obrót w pionie aby uniknąć przewrócenia kamery
      euler.current.x = Math.max(
        -Math.PI / 2 + 0.1,
        Math.min(Math.PI / 2 - 0.1, euler.current.x),
      );

      // Zastosuj obrót do kamery poprzez płynne interpolowanie
      const currentQuat = camera.quaternion.clone();
      const targetQuat = new THREE.Quaternion().setFromEuler(euler.current);
      currentQuat.slerp(targetQuat, 0.5); // Płynniejsza interpolacja
      camera.quaternion.copy(currentQuat);

      // Aktualizuj pozycję myszy
      rightMousePosition.current = { x: e.clientX, y: e.clientY };

      // Ustaw flagę patrzenia
      setIsLookingAround(true);
    };

    const handleMouseUp = (e) => {
      if (e.button === 2) {
        // Prawy przycisk
        setRightMousePressed(false);

        // Wyłączamy tryb "patrzenia dookoła" z opóźnieniem,
        // aby uniknąć konfliktów z innymi kontrolami
        setTimeout(() => {
          setIsLookingAround(false);
        }, 100);
      }
    };

    // Dokument, a nie canvas, aby złapać zdarzenie nawet gdy mysz wyjdzie poza canvas
    canvas.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      canvas.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [camera, gl, rightMousePressed, setIsLookingAround, rotationSpeed]);

  // Renderuj falę teleportacji
  return (
    <>
      <Html></Html>
      {teleportPoint && <TeleportationWave point={teleportPoint} />}
    </>
  );
}

// Główny komponent renderera dla urządzeń mobilnych
const MobileRenderer = () => {
  const [walls, setWalls] = useState([]);
  const [doors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setIsLookingAround] = useState(false);
  const [useDeviceOrientation, setUseDeviceOrientation] = useState(false);
  const [vrModeActive, setVrModeActive] = useState(false); // Stan dla trybu VR
  const [teleportEnabled, setTeleportEnabled] = useState(false); // Stan dla teleportacji
  const moveInterval = useRef(null);
  const [, setIsMovingForward] = useState(false);
  const [, setIsMovingBackward] = useState(false);
  const animationRef = useRef(null);
  const [localTeleportWavePoint, setLocalTeleportWavePoint] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showWelcomeMenu, setShowWelcomeMenu] = useState(true); // Stan dla menu powitalnego
  // Stan modelLoaded kontroluje wyświetlanie menu powitalnego
  const [modelLoaded, setModelLoaded] = useState(false);

  // Funkcja przełączająca tryb teleportacji
  const toggleTeleport = () => {
    const newTeleportState = !teleportEnabled;
    setTeleportEnabled(newTeleportState);
    console.log("Teleportacja:", newTeleportState ? "włączona" : "wyłączona");
  };

  // Funkcje ruchu
  const moveForward = useCallback(() => {
    const camera = window.mobileCamera;
    if (!camera) return;

    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);
    direction.y = 0;
    direction.normalize();

    // Dostosowana prędkość ruchu dla płynności
    const moveSpeed = 0.15;
    const newPosition = camera.position
      .clone()
      .add(direction.multiplyScalar(moveSpeed));

    // Sprawdzenie kolizji z większą precyzją
    if (!checkCollision(camera.position, newPosition, walls, doors, 0.2)) {
      camera.position.lerp(newPosition, 0.8); // Płynna interpolacja ruchu
    } else {
      // Próba ruchu w osiach X i Z osobno dla lepszego zachowania przy kolizjach
      const xMove = new THREE.Vector3(direction.x * moveSpeed, 0, 0);
      const zMove = new THREE.Vector3(0, 0, direction.z * moveSpeed);

      const xTarget = camera.position.clone().add(xMove);
      const zTarget = camera.position.clone().add(zMove);

      if (!checkCollision(camera.position, xTarget, walls, doors, 0.2)) {
        camera.position.x += xMove.x * 0.8;
      }
      if (!checkCollision(camera.position, zTarget, walls, doors, 0.2)) {
        camera.position.z += zMove.z * 0.8;
      }
    }
  }, [walls, doors]);

  const moveBackward = useCallback(() => {
    const camera = window.mobileCamera;
    if (!camera) return;

    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);
    direction.negate();
    direction.y = 0;
    direction.normalize();

    // Dostosowana prędkość ruchu dla płynności
    const moveSpeed = 0.15;
    const newPosition = camera.position
      .clone()
      .add(direction.multiplyScalar(moveSpeed));

    // Sprawdzenie kolizji z większą precyzją
    if (!checkCollision(camera.position, newPosition, walls, doors, 0.2)) {
      camera.position.lerp(newPosition, 0.8); // Płynna interpolacja ruchu
    } else {
      // Próba ruchu w osiach X i Z osobno dla lepszego zachowania przy kolizjach
      const xMove = new THREE.Vector3(direction.x * moveSpeed, 0, 0);
      const zMove = new THREE.Vector3(0, 0, direction.z * moveSpeed);

      const xTarget = camera.position.clone().add(xMove);
      const zTarget = camera.position.clone().add(zMove);

      if (!checkCollision(camera.position, xTarget, walls, doors, 0.2)) {
        camera.position.x += xMove.x * 0.8;
      }
      if (!checkCollision(camera.position, zTarget, walls, doors, 0.2)) {
        camera.position.z += zMove.z * 0.8;
      }
    }
  }, [walls, doors]);

  // Obsługa ciągłego ruchu przy przytrzymaniu
  const startContinuousMove = useCallback((moveFunction, setStateFunction) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    setStateFunction(true);
    let lastTime = performance.now();
    const targetFrameTime = 1000 / 60; // Celujemy w 60 FPS

    const animate = (currentTime) => {
      const deltaTime = currentTime - lastTime;

      if (deltaTime >= targetFrameTime) {
        moveFunction();
        lastTime = currentTime;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
  }, []);

  const stopContinuousMove = useCallback((setStateFunction) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    setStateFunction(false);
  }, []);

  // Czyszczenie animacji przy odmontowaniu
  useEffect(() => {
    const currentAnimation = animationRef.current;
    const currentInterval = moveInterval.current;
    return () => {
      if (currentAnimation) {
        cancelAnimationFrame(currentAnimation);
      }
      if (currentInterval) {
        clearInterval(currentInterval);
      }
    };
  }, []);

  // Obsługa przełącznika orientacji urządzenia
  const toggleDeviceOrientation = async () => {
    if (useDeviceOrientation) {
      // Jeśli orientacja jest włączona, po prostu ją wyłączamy
      setUseDeviceOrientation(false);
      setVrModeActive(false);
      return;
    }

    // Sprawdzamy czy przeglądarka wspiera czujniki orientacji
    if (!window.DeviceOrientationEvent) {
      alert("Twoje urządzenie nie obsługuje czujników orientacji.");
      return;
    }

    try {
      // Obsługa dla iOS (wymaga requestPermission)
      if (typeof DeviceOrientationEvent.requestPermission === "function") {
        try {
          const response = await DeviceOrientationEvent.requestPermission();
          if (response === "granted") {
            setUseDeviceOrientation(true);
            setVrModeActive(true);
          } else {
            alert("Dostęp do czujników został odrzucony.");
          }
        } catch (error) {
          console.error("Błąd podczas żądania uprawnień:", error);
          alert("Nie udało się uzyskać dostępu do czujników.");
        }
      } else {
        // Obsługa dla Android i innych platform
        setUseDeviceOrientation(true);
        setVrModeActive(true);

        const orientationHandler = (event) => {
          console.log("Odpowiedź z czujników orientacji:", {
            alpha: event.alpha,
            beta: event.beta,
            gamma: event.gamma,
          });
          window.removeEventListener("deviceorientation", orientationHandler);
        };

        window.addEventListener("deviceorientation", orientationHandler, {
          once: true,
        });
      }
    } catch (error) {
      console.error("Błąd podczas inicjalizacji czujników:", error);
      alert("Wystąpił błąd podczas próby dostępu do czujników orientacji.");
    }
  };

  // Ukryj overlay ładowania po pewnym czasie
  useEffect(() => {
    const timer = setTimeout(() => {
      const loadingOverlay = document.getElementById("loading-overlay");
      if (loadingOverlay) {
        loadingOverlay.style.opacity = 0;
        setTimeout(() => {
          loadingOverlay.style.display = "none";
        }, 500);
      }
      setLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Funkcja wywołana po utworzeniu canvasa
  const handleCreated = () => {
    console.log("Canvas zostało utworzone");
  };

  // Dodaję obsługę zamykania menu po kliknięciu poza nim
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (e) => {
      // Sprawdź czy kliknięcie było poza menu i nie było na przycisku menu
      const menuElement = document.querySelector(".hamburger-menu");
      const menuButton = document.querySelector(".hamburger-menu-button");

      if (
        menuElement &&
        !menuElement.contains(e.target) &&
        menuButton &&
        !menuButton.contains(e.target)
      ) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showMenu]);

  // Obsługa kliknięcia przycisku "Rozpocznij zwiedzanie"
  const handleStartExploring = () => {
    setShowWelcomeMenu(false);
  };

  // Funkcja do wyjścia z eksploratora i powrotu do strony głównej
  const exitExplorer = () => {
    console.log("Wyjście do strony głównej");
    window.location.href = "/";
  };

  // Wyświetlanie menu powitalnego po załadowaniu
  useEffect(() => {
    if (!loading && modelLoaded) {
      console.log("Pokazuję menu powitalne");
      setShowWelcomeMenu(true);
    }
  }, [loading, modelLoaded]);

  // Ukryj ekran ładowania po załadowaniu modelu (wykryciu ścian)
  useEffect(() => {
    if (walls.length > 0 && !modelLoaded) {
      console.log("Model został załadowany - ukrywam ekran ładowania");

      // Opóźniamy ukrycie ekranu ładowania o 1 sekundę po załadowaniu modelu
      setTimeout(() => {
        const loadingOverlay = document.getElementById("loading-overlay");
        if (loadingOverlay) {
          loadingOverlay.style.opacity = 0;
          setTimeout(() => {
            loadingOverlay.style.display = "none";
            // Ustawiamy zmienne stanu dopiero po ukryciu ekranu ładowania
            setLoading(false);
            setModelLoaded(true);
          }, 500);
        }
      }, 1000);
    }
  }, [walls, modelLoaded]);

  return (
    <div className="mobile-renderer-container">
      <Canvas
        className="mobile-canvas"
        camera={{
          position: [0, 1.65, 2.0],
          rotation: [0, 0, 0],
          fov: 75,
          near: 0.1,
          far: 1000,
        }}
        onCreated={handleCreated}
      >
        <Suspense fallback={<LoadingIndicator />}>
          <color attach="background" args={["#cfc9c0"]} />
          {/* Model nie zawiera własnych świateł, więc oświetlenie sceny ustawiamy ręcznie */}
          <ambientLight intensity={0.8} color="#fff6e6" />
          <hemisphereLight
            skyColor="#fff0dd"
            groundColor="#e0e0ff"
            intensity={0.6}
            position={[0, 50, 0]}
          />
          <directionalLight
            position={[3, 4.5, -3]}
            intensity={1.2}
            color="#fff2df"
            castShadow
            shadow-mapSize-width={1024}
            shadow-mapSize-height={1024}
            shadow-camera-near={0.5}
            shadow-camera-far={20}
            shadow-camera-left={-6}
            shadow-camera-right={6}
            shadow-camera-top={6}
            shadow-camera-bottom={-6}
          />
          {/* Światło uzupełniające z przeciwnej strony, by druga strona pokoju nie była czarna */}
          <directionalLight position={[-3, 3, 4]} intensity={0.7} color="#e7ecff" />
          <Model setWalls={setWalls} />

          {!loading && (
            <TouchControls
              walls={walls}
              doors={doors}
              setIsLookingAround={setIsLookingAround}
              enableDeviceOrientation={useDeviceOrientation}
              teleportEnabled={teleportEnabled}
              updateTeleportWavePoint={setLocalTeleportWavePoint}
            />
          )}
        </Suspense>

        {teleportEnabled && localTeleportWavePoint && (
          <TeleportationWave point={localTeleportWavePoint} />
        )}
      </Canvas>

      <div className="hud-interface">
        {!showWelcomeMenu && (
          <>
            <div className="navigation-controls">
              <div
                className="nav-button up"
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  moveForward();
                  setIsMovingForward(true);
                  startContinuousMove(moveForward, setIsMovingForward);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  stopContinuousMove(setIsMovingForward);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  moveForward();
                  setIsMovingForward(true);
                  startContinuousMove(moveForward, setIsMovingForward);
                }}
                onMouseUp={(e) => {
                  e.preventDefault();
                  stopContinuousMove(setIsMovingForward);
                }}
                onMouseLeave={(e) => {
                  e.preventDefault();
                  stopContinuousMove(setIsMovingForward);
                }}
              />
              <div
                className="nav-button down"
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  moveBackward();
                  setIsMovingBackward(true);
                  startContinuousMove(moveBackward, setIsMovingBackward);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  stopContinuousMove(setIsMovingBackward);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  moveBackward();
                  setIsMovingBackward(true);
                  startContinuousMove(moveBackward, setIsMovingBackward);
                }}
                onMouseUp={(e) => {
                  e.preventDefault();
                  stopContinuousMove(setIsMovingBackward);
                }}
                onMouseLeave={(e) => {
                  e.preventDefault();
                  stopContinuousMove(setIsMovingBackward);
                }}
              />
            </div>

            {/* Przycisk hamburger menu */}
            <div
              className="hamburger-menu-button"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
            >
              <div className="hamburger-icon">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>

            {/* Menu hamburgerowe */}
            {showMenu && (
              <div className="hamburger-menu">
                <div className="menu-header">
                  <h3>Opcje sterowania</h3>
                  <div
                    className="close-menu-button"
                    onClick={() => setShowMenu(false)}
                  >
                    ✕
                  </div>
                </div>

                <div className="menu-options">
                  {/* Opcja teleportacji */}
                  <div className="menu-option">
                    <div className="option-label">Teleportacja</div>
                    <div
                      className={`option-toggle ${
                        teleportEnabled ? "active" : ""
                      }`}
                      onClick={() => toggleTeleport()}
                    >
                      <div className="toggle-slider"></div>
                    </div>
                  </div>

                  {/* Opcja trybu VR */}
                  <div className="menu-option">
                    <div className="option-label">
                      Tryb VR
                      <br />
                      (sterowanie żyroskopem)
                    </div>
                    <div
                      className={`option-toggle ${
                        vrModeActive ? "active" : ""
                      }`}
                      onClick={() => toggleDeviceOrientation()}
                    >
                      <div className="toggle-slider"></div>
                    </div>
                  </div>

                  {/* Opcja wyjścia */}
                  <div
                    className="menu-option exit-option"
                    onClick={exitExplorer}
                  >
                    <div className="option-label exit-label">
                      Wyjdź do strony głównej
                    </div>
                    <div className="menu-icon">🚪</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Menu powitalne */}
        {showWelcomeMenu && (
          <div className="welcome-menu">
            <div className="welcome-content">
              <h2>Opcje sterowania</h2>

              <div className="controls-section">
                <div className="welcome-options">
                  {/* Opcja rozglądania się - zawsze włączona */}
                  <div className="welcome-option active-option">
                    <div className="option-label">
                      Przeciągnij palcem po ekranie, aby rozglądać się dookoła
                    </div>
                    <div className="option-toggle active always-on">
                      <div className="toggle-slider"></div>
                    </div>
                    <p className="option-description">
                      Przeciągnij palcem po ekranie, aby obracać kamerę i
                      oglądać otoczenie. Użyj strzałek po lewej stronie do
                      poruszania się do przodu i do tyłu.
                    </p>
                  </div>

                  {/* Opcja trybu VR */}
                  <div className="welcome-option">
                    <div className="option-label">
                      Tryb VR
                      <br />
                      (sterowanie żyroskopem)
                    </div>
                    <div
                      className={`option-toggle ${
                        vrModeActive ? "active" : ""
                      }`}
                      onClick={() => toggleDeviceOrientation()}
                    >
                      <div className="toggle-slider"></div>
                    </div>
                    <p className="option-description">
                      Gdy włączony, poruszaj telefonem, aby rozglądać się
                      dookoła
                    </p>
                  </div>

                  {/* Opcja teleportacji */}
                  <div className="welcome-option">
                    <div className="option-label">Teleportacja</div>
                    <div
                      className={`option-toggle ${
                        teleportEnabled ? "active" : ""
                      }`}
                      onClick={() => toggleTeleport()}
                    >
                      <div className="toggle-slider"></div>
                    </div>
                    <p className="option-description">
                      Gdy włączona, kliknij na podłogę, aby przenieść się w
                      wybrane miejsce
                    </p>
                  </div>

                  {/* Opcja menu - zawsze włączona */}
                  <div className="welcome-option active-option">
                    <div className="option-label">
                      Użyj menu w prawym dolnym rogu
                    </div>
                    <div className="option-toggle active always-on">
                      <div className="toggle-slider"></div>
                    </div>
                    <p className="option-description">
                      Użyj menu (przycisk w prawym dolnym rogu), aby zmienić
                      ustawienia w trakcie zwiedzania
                    </p>
                  </div>
                </div>
              </div>

              <div className="welcome-actions">
                <button
                  className="start-exploring-button"
                  onClick={handleStartExploring}
                >
                  Rozpocznij zwiedzanie
                </button>
                <button className="exit-gallery-button" onClick={exitExplorer}>
                  Wyjdź do strony głównej
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div id="loading-overlay" className="loading-overlay"></div>
    </div>
  );
};

export default MobileRenderer;
